import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';

import { State } from 'redux/initialState';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';

import menuTypes from 'constants/menuTypes';
import { TradingOffer } from 'types';
import { TradingOffersListContextType } from '../types/TradingOffersListContextType';
import { tradingOffersListInitValue } from '../constants/TradingOffersListInitValue';
import { TradingOfferSortOption } from '../constants/tradingOffersSortOptions';
import {
  TradingOffersSummary,
  PartnerAgreedTradingOffer,
  TradingOfferType,
} from '../../../types/TradingOffer';
import { useGetTradingOffers } from '../hooks/useGetTradingOffers';

const TRADING_OFFERS_PAGE_SIZE = 100;
const TRADING_OFFERS_FILTERS: string[] = [];

export const TradingOffersListContext =
  createContext<TradingOffersListContextType>(tradingOffersListInitValue);

export const TradingOffersListProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const history = useHistory();
  const { societyId, userId } = useParams<{
    societyId: string;
    userId: string;
  }>();

  const actualSociety = useSelector(
    ({ society }: State) => society?.actualSociety
  );
  const user = useSelector(({ user }: State) => user);
  const tradingOffers = useSelector(
    ({ tradingOffers }: State) => (tradingOffers ?? []) as TradingOffer[]
  );

  const partnerOperations = useMemo<PartnerAgreedTradingOffer[]>(() => [], []);
  const [sellOffersSortBy, setSellOffersSortBy] =
    useState<TradingOfferSortOption>('price');
  const [buyOffersSortBy, setBuyOffersSortBy] =
    useState<TradingOfferSortOption>('price');

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const { isLoading: isTradingOffersLoading } = useGetTradingOffers({
    societyId: actualSociety ? societyId : undefined,
    page: 1,
    rowsPerPage: TRADING_OFFERS_PAGE_SIZE,
    filters: TRADING_OFFERS_FILTERS,
  });

  const isAdmin = useMemo(() => {
    const role = actualSociety?.administrators?.find(
      (admin: Record<string, string>) => admin?.user === user?.['_id']
    );
    return !!role;
  }, [actualSociety, user]);

  const isReadOnly = false;

  const sellOffers = useMemo(
    () => tradingOffers.filter((offer) => offer.type === TradingOfferType.SELL),
    [tradingOffers]
  );

  const buyOffers = useMemo(
    () => tradingOffers.filter((offer) => offer.type === TradingOfferType.BUY),
    [tradingOffers]
  );

  const calculateSummary = useCallback(
    (offers: TradingOffer[]): TradingOffersSummary => {
      const offersCount = offers.length;
      const sharesCount = offers.reduce(
        (acc, offer) => acc + offer.sharesCount.total,
        0
      );
      const totalAmount = offers.reduce(
        (acc, offer) => acc + offer.sharesCount.total * offer.sharePrice,
        0
      );

      return { offersCount, sharesCount, totalAmount };
    },
    []
  );

  const sellOffersSummary = useMemo(
    () => calculateSummary(sellOffers),
    [sellOffers, calculateSummary]
  );

  const buyOffersSummary = useMemo(
    () => calculateSummary(buyOffers),
    [buyOffers, calculateSummary]
  );

  const activeOperationsCount = useMemo(
    () => partnerOperations.filter((op) => !op.isApproved).length,
    [partnerOperations]
  );

  const totalOperationsCount = useMemo(
    () => partnerOperations.length,
    [partnerOperations]
  );

  const sortOffers = useCallback(
    (offers: TradingOffer[], sortBy: TradingOfferSortOption): TradingOffer[] =>
      [...offers].sort((a, b) => {
        switch (sortBy) {
          case 'price':
            return b.sharePrice - a.sharePrice;
          case 'date':
            return (
              new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
            );
          case 'shareClass':
            return String(a.shareClass).localeCompare(String(b.shareClass));
          default:
            return 0;
        }
      }),
    []
  );

  const sortedSellOffers = useMemo(
    () => sortOffers(sellOffers, sellOffersSortBy),
    [sellOffers, sellOffersSortBy, sortOffers]
  );

  const sortedBuyOffers = useMemo(
    () => sortOffers(buyOffers, buyOffersSortBy),
    [buyOffers, buyOffersSortBy, sortOffers]
  );

  const handleCreateSellOffer = useCallback(() => {
    history.push(`/crear-oferta-venta/${societyId}`);
  }, [history, societyId]);

  const handleCreateBuyOffer = useCallback(() => {
    history.push(`/crear-oferta-compra/${societyId}`);
  }, [history, societyId]);

  const handleViewOfferDetail = useCallback(
    (offerId: string) => {
      history.push(`/detalle-oferta/${societyId}/${offerId}`);
    },
    [history, societyId]
  );

  const handleSellOffersSortChange = useCallback(
    (sortBy: TradingOfferSortOption) => {
      setSellOffersSortBy(sortBy);
    },
    []
  );

  const handleBuyOffersSortChange = useCallback(
    (sortBy: TradingOfferSortOption) => {
      setBuyOffersSortBy(sortBy);
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      data: {
        societyId: societyId || '',
        societyName: actualSociety?.name || '',
        sellOffers: sortedSellOffers,
        buyOffers: sortedBuyOffers,
        partnerOperations,
        sellOffersSummary,
        buyOffersSummary,
        activeOperationsCount,
        totalOperationsCount,
        sellOffersSortBy,
        buyOffersSortBy,
        isAdmin,
        isReadOnly,
        isLoading: isTradingOffersLoading || !actualSociety,
      },
      actions: {
        handleCreateSellOffer,
        handleCreateBuyOffer,
        handleViewOfferDetail,
        handleSellOffersSortChange,
        handleBuyOffersSortChange,
      },
    }),
    [
      societyId,
      actualSociety,
      sortedSellOffers,
      sortedBuyOffers,
      partnerOperations,
      sellOffersSummary,
      buyOffersSummary,
      activeOperationsCount,
      totalOperationsCount,
      sellOffersSortBy,
      buyOffersSortBy,
      isAdmin,
      isReadOnly,
      isTradingOffersLoading,
      handleCreateSellOffer,
      handleCreateBuyOffer,
      handleViewOfferDetail,
      handleSellOffersSortChange,
      handleBuyOffersSortChange,
    ]
  );

  return (
    <TradingOffersListContext.Provider value={contextValue}>
      {children}
    </TradingOffersListContext.Provider>
  );
};

export const useTradingOffersList = () => {
  const context = useContext(TradingOffersListContext);
  if (!context) {
    throw new Error(
      'useTradingOffersList must be used within a TradingOffersListProvider'
    );
  }
  return context;
};
